import { useRouteBodyState } from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import type { ReferenceGraphemeMorphemeSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { MorphemeInfoPanelCompositionCompoundsListView } from "#morpheme-info-panel-composition-compounds-list-view";
import { getMorphemeInfoPanelCompositionCompoundsListViewGate } from "#get-morpheme-info-panel-composition-compounds-list-view-gate";
import type { MorphemeInfoPanelAccordionSectionDescriptor } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content._components.morpheme-info-panel-accordion";
import { useRevealedMorphemeCompoundCards } from "@-/dev.improve.haniverse.lib.morpheme.read";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import { View } from "@-/com.npmjs.react-native";

export function useMorphemeInfoPanelCompositionCompoundsSection({
  graphemeCard,
}: {
  graphemeCard: ReferenceGraphemeMorphemeSlice;
}): MorphemeInfoPanelAccordionSectionDescriptor & {
  /** How many compounds the section lists; `undefined` until the list has loaded. */
  compoundCount: number | undefined;
  /**
   * The header's filter menu ("Show rare compounds"), or `undefined` where it would change
   * nothing: a character whose every compound is already in the browsing corpus.
   */
  rareCompoundsFilter:
    | {
        isShowingRareCompounds: boolean;
        setIsShowingRareCompounds: (isShowingRareCompounds: boolean) => void;
      }
    | undefined;
} {
  // The section never collapses: its header toggles between the compact glyph grid (default) and
  // the full detail tiles, shown as a plus/minus in place of the chevron.
  const [isExpanded, setIsExpanded] = useRouteBodyState(
    "use-morpheme-info-panel-composition-compounds-section.tsx:isExpanded",
    false,
  );
  const [isShowingRareCompounds, setIsShowingRareCompounds] = useDeviceSetting(
    "showRareCompoundCharactersEnabled",
  );
  const {
    cards,
    compoundCharacterList,
    hasMore,
    isCardsPending,
    isPending,
    rareCompoundCount,
    revealMore,
  } = useRevealedMorphemeCompoundCards({
    enabled: true,
    graphemeId: graphemeCard.graphemeId,
    includeRareCompounds: isShowingRareCompounds,
  });
  const toggleRareCompounds = () => setIsShowingRareCompounds(!isShowingRareCompounds);

  const listViewGate = getMorphemeInfoPanelCompositionCompoundsListViewGate({
    cards,
    compoundCharacterList,
    hasMore,
    isCardsPending,
  });

  return {
    bodyKey: "morpheme-info-composition-compounds-body",
    // An empty list is only final once nothing is pending; a non-empty one is already complete.
    compoundCount:
      !isPending || compoundCharacterList.length > 0 ? compoundCharacterList.length : undefined,
    headerKey: "morpheme-info-composition-compounds-header",
    iconName: "LuComponent",
    // While the first page of compound cards is still loading, show a spinner in place of the
    // chevron rather than surfacing loading copy in the body.
    isLoading: isCardsPending && cards.length === 0,
    isOpen: true,
    // A common component has over a thousand compounds; the body reveals them a page at a time as
    // this section scrolls toward the bottom, so every compound stays reachable.
    onEndReached: revealMore,
    onToggle: () => setIsExpanded((previousState) => !previousState),
    renderBody: () => (
      <View className="bg-surface p-1">
        {listViewGate !== null && (
          <MorphemeInfoPanelCompositionCompoundsListView
            gate={listViewGate}
            isExpanded={isExpanded}
            onRevealMore={revealMore}
            onShowRareCompounds={
              !isShowingRareCompounds && rareCompoundCount !== undefined && rareCompoundCount > 0
                ? toggleRareCompounds
                : undefined
            }
            rareCompoundCount={rareCompoundCount ?? 0}
          />
        )}
      </View>
    ),
    // Only offered where it changes the list: a character whose every compound is already in the
    // browsing corpus has nothing to reveal.
    rareCompoundsFilter:
      rareCompoundCount !== undefined && rareCompoundCount > 0
        ? { isShowingRareCompounds, setIsShowingRareCompounds }
        : undefined,
    title: "Compounds",
    trailingIconName: isExpanded ? "LuMinus" : "LuPlus",
  };
}
